import { supabase } from '../supabase';

export type ShipsGoWorkerType = 'discovery' | 'registration' | 'observation';

export type ShipsGoRunStatus = 'running' | 'succeeded' | 'partial' | 'failed';

export interface ShipsGoOperationsSummary {
  total: number;
  scheduled: number;
  queued: number;
  registered: number;
  observing: number;
  waiting_provider_data: number;
  returned: number;
  completed: number;
  attention: number;
  ocean: number;
  air: number;
}

export interface ShipsGoCreditSummary {
  credits_consumed_30d: number;
  latest_credits_remaining: number | null;
  latest_credit_observed_at: string | null;
}

export interface ShipsGoExporterOperations {
  exporter_id: number;
  exporter_name: string;
  total: number;
  registered: number;
  observing: number;
  waiting_provider_data: number;
  returned: number;
  attention: number;
  last_activity_at: string | null;
}

export interface ShipsGoWorkerRun {
  id: string;
  worker_type: ShipsGoWorkerType;
  trigger_source: 'scheduled' | 'targeted' | 'manual';
  requested_limit: number;
  selected_count: number;
  succeeded_count: number;
  failed_count: number;
  status: ShipsGoRunStatus;
  error_message: string | null;
  started_at: string;
  completed_at: string | null;
  metadata: Record<string, unknown>;
}

export interface ShipsGoRegistrationAttemptSummary {
  id: string;
  attempt_number: number;
  result: string;
  http_status: number | null;
  provider_credit_cost: number | null;
  provider_credits_remaining: number | null;
  error_code: string | null;
  error_message: string | null;
  attempted_at: string;
  completed_at: string | null;
}

export interface ShipsGoJourneyLink {
  shipment_id: number | null;
  reference_number: string | null;
  season: string | null;
  species_id: number | null;
  species_name: string | null;
  source_system: string;
  linked_at: string;
  last_seen_at: string;
}

export interface ShipsGoJourneyOperations {
  id: string;
  exporter_id: number;
  exporter_name: string;
  mode: 'ocean' | 'air';
  identifier_type: 'container' | 'awb';
  identifier_value: string;
  status: string;
  provider_tracking_id: string | null;
  provider_status: string | null;
  provider_container_status: string | null;
  carrier_code: string | null;
  carrier_name: string | null;
  origin_name: string | null;
  destination_name: string | null;
  planned_etd: string;
  planned_eta: string;
  live_etd: string | null;
  live_eta: string | null;
  registration_opens_on: string;
  registration_closes_on: string;
  registration_attempt_count: number;
  next_registration_attempt_at: string | null;
  last_registration_attempt_at: string | null;
  registered_at: string | null;
  observation_status: string;
  observation_closes_on: string | null;
  cargo_arrived_at: string | null;
  gate_out_at: string | null;
  empty_return_at: string | null;
  last_sync_attempt_at: string | null;
  last_sync_succeeded_at: string | null;
  consecutive_sync_failures: number;
  last_error_code: string | null;
  last_error_message: string | null;
  latest_attempt: ShipsGoRegistrationAttemptSummary | null;
  event_count: number;
  actual_event_count: number;
  waiting_for_provider_data: boolean;
  provider_data_attention: boolean;
  container_snapshot_count: number;
  last_container_snapshot_at: string | null;
  links: ShipsGoJourneyLink[];
  created_at: string;
  updated_at: string;
}

export interface ShipsGoOperationsPayload {
  generated_at: string;
  summary: ShipsGoOperationsSummary;
  credits: ShipsGoCreditSummary;
  exporters: ShipsGoExporterOperations[];
  runs: ShipsGoWorkerRun[];
  journeys: ShipsGoJourneyOperations[];
  pagination: {
    limit: number;
    offset: number;
    total: number;
  };
}

export interface ShipsGoOperationsFilters {
  exporterId?: number;
  status?: string;
  mode?: 'ocean' | 'air';
  search?: string;
  limit?: number;
  offset?: number;
  runsSince?: string;
}

export async function getSuperadminShipsGoTrackingOperations(
  filters: ShipsGoOperationsFilters = {}
): Promise<ShipsGoOperationsPayload> {
  const { data, error } = await supabase.rpc(
    'get_superadmin_shipsgo_tracking_operations',
    {
      p_exporter_id: filters.exporterId ?? null,
      p_status: filters.status ?? null,
      p_mode: filters.mode ?? null,
      p_search: filters.search?.trim() || null,
      p_limit: filters.limit ?? 50,
      p_offset: filters.offset ?? 0,
      p_runs_since:
        filters.runsSince ??
        new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString(),
    }
  );

  if (error) throw error;
  if (!data || typeof data !== 'object') {
    throw new Error('ShipsGo operations dashboard returned no data');
  }

  return data as unknown as ShipsGoOperationsPayload;
}
